import { buildShellTemplatePreviewHtml } from 'features/EmailEditor/buildShellTemplatePreviewHtml'
import { getTemplateOptions } from 'features/EmailEditor/templates'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import styles from './EmailEditor.module.scss'

const EMAIL_WIDTH = 500
const FALLBACK_PANEL_WIDTH = 440
const VIEWPORT_PADDING = 16
const PREVIEW_CACHE_VERSION = 17

const previewHtmlCache = new Map()

function resolvePreviewHtml(templateKey) {
  if (!templateKey) {
    return ''
  }

  const cacheKey = `${PREVIEW_CACHE_VERSION}:${templateKey}`

  if (!previewHtmlCache.has(cacheKey)) {
    previewHtmlCache.set(cacheKey, buildShellTemplatePreviewHtml(templateKey))
  }

  return previewHtmlCache.get(cacheKey)
}

function measurePreviewDimensions(contentElement, viewportElement) {
  const measuredHeight = contentElement.scrollHeight
  const availableWidth = Math.max(
    (viewportElement?.clientWidth || FALLBACK_PANEL_WIDTH) - VIEWPORT_PADDING,
    120
  )
  const scale = Math.min(availableWidth / EMAIL_WIDTH, 1)
  const scaledHeight = Math.ceil(measuredHeight * scale)

  return {
    scale,
    contentHeight: measuredHeight,
    scaledHeight
  }
}

const TemplatePickerSidePreview = ({ templateKey }) => {
  const viewportRef = useRef(null)
  const contentRef = useRef(null)
  const [dimensions, setDimensions] = useState({
    scale: 0.64,
    contentHeight: 480,
    scaledHeight: 300
  })

  const templateOptions = getTemplateOptions()
  const activeOption =
    templateOptions.find(option => option.value === templateKey) || templateOptions[0]

  const previewHtml = useMemo(
    () => (templateKey ? resolvePreviewHtml(templateKey) : ''),
    [templateKey]
  )

  useLayoutEffect(() => {
    if (!templateKey) {
      return
    }

    setDimensions({
      scale: 0.64,
      contentHeight: 480,
      scaledHeight: 300
    })
  }, [templateKey])

  useLayoutEffect(() => {
    if (!templateKey || !contentRef.current) {
      return
    }

    let resizeFrame = 0

    const measure = () => {
      if (!contentRef.current) {
        return
      }

      setDimensions(measurePreviewDimensions(contentRef.current, viewportRef.current))
    }

    const scheduleMeasure = () => {
      cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(measure)
    }

    measure()

    const animationFrame = requestAnimationFrame(measure)
    const imageLoadTimeout = window.setTimeout(measure, 120)

    window.addEventListener('resize', scheduleMeasure)

    const contentNode = contentRef.current
    const images = contentNode ? contentNode.querySelectorAll('img') : []
    images.forEach(image => {
      if (!image.complete) {
        image.addEventListener('load', scheduleMeasure)
      }
    })

    return () => {
      cancelAnimationFrame(animationFrame)
      cancelAnimationFrame(resizeFrame)
      window.clearTimeout(imageLoadTimeout)
      window.removeEventListener('resize', scheduleMeasure)
      images.forEach(image => {
        image.removeEventListener('load', scheduleMeasure)
      })
    }
  }, [previewHtml, templateKey])

  if (!templateKey) {
    return (
      <aside className={styles.templatePickerSidePreview}>
        <p className={styles.templatePickerSidePreviewEmpty}>
          Hover a template to preview its layout.
        </p>
      </aside>
    )
  }

  const { scale, scaledHeight } = dimensions
  const scaledWidth = EMAIL_WIDTH * scale

  return (
    <aside className={styles.templatePickerSidePreview}>
      <div className={styles.templatePickerSidePreviewHeader}>
        <span className={styles.templatePickerSidePreviewName}>{activeOption.label}</span>
        <span className={styles.templatePickerSidePreviewDescription}>
          {activeOption.description}
        </span>
      </div>
      <div className={styles.templatePickerSidePreviewViewport} ref={viewportRef}>
        <div
          className={styles.templatePickerSidePreviewFrame}
          style={{ width: scaledWidth, height: scaledHeight }}
        >
          <div
            key={templateKey}
            ref={contentRef}
            className={[styles.templatePickerSidePreviewContent, 'no-style-reset'].join(' ')}
            style={{
              width: EMAIL_WIDTH,
              transform: `scale(${scale})`
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      </div>
    </aside>
  )
}

export default TemplatePickerSidePreview
